const ALLOWED_TAGS = new Set([
    "a",
    "b",
    "blockquote",
    "br",
    "code",
    "div",
    "em",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
    "i",
    "img",
    "li",
    "ol",
    "p",
    "pre",
    "span",
    "strong",
    "table",
    "tbody",
    "td",
    "tfoot",
    "th",
    "thead",
    "tr",
    "u",
    "ul",
]);

const GLOBAL_ALLOWED_ATTRIBUTES = new Set(["class", "title", "aria-label"]);
const TAG_ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
    a: new Set(["href", "target", "rel"]),
    img: new Set(["src", "alt", "width", "height"]),
    td: new Set(["colspan", "rowspan"]),
    th: new Set(["colspan", "rowspan", "scope"]),
};

const URL_ATTRIBUTES = new Set(["href", "src"]);
const SAFE_URL_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

const isSafeUrl = (value: string): boolean => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return false;
    }

    if (trimmedValue.startsWith("#") || trimmedValue.startsWith("/") || trimmedValue.startsWith("./") || trimmedValue.startsWith("../")) {
        return true;
    }

    try {
        const url = new URL(trimmedValue, window.location.origin);
        return SAFE_URL_PROTOCOLS.has(url.protocol);
    } catch (_error) {
        return false;
    }
};

const sanitizeElementAttributes = (element: Element): void => {
    const tagName = element.tagName.toLowerCase();
    const allowedAttributes = TAG_ALLOWED_ATTRIBUTES[tagName] ?? new Set<string>();

    Array.from(element.attributes).forEach((attribute) => {
        const attributeName = attribute.name.toLowerCase();
        const attributeValue = attribute.value;
        const isAllowed = GLOBAL_ALLOWED_ATTRIBUTES.has(attributeName) || allowedAttributes.has(attributeName);

        if (!isAllowed || attributeName.startsWith("on")) {
            element.removeAttribute(attribute.name);
            return;
        }

        if (URL_ATTRIBUTES.has(attributeName) && !isSafeUrl(attributeValue)) {
            element.removeAttribute(attribute.name);
        }
    });

    if (tagName === "a" && element.getAttribute("target") === "_blank") {
        element.setAttribute("rel", "noopener noreferrer");
    }
};

const sanitizeNode = (node: Node): void => {
    Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
            const element = child as Element;
            const tagName = element.tagName.toLowerCase();

            if (!ALLOWED_TAGS.has(tagName)) {
                element.replaceWith(...Array.from(element.childNodes));
                sanitizeNode(node);
                return;
            }

            sanitizeElementAttributes(element);
            sanitizeNode(element);
            return;
        }

        if (child.nodeType !== Node.TEXT_NODE) {
            child.remove();
        }
    });
};

export const sanitizeHtml = (html: string): string => {
    if (typeof window === "undefined" || !html) {
        return "";
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    sanitizeNode(doc.body);

    return doc.body.innerHTML;
};
